import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, Clock } from "lucide-react";
import { getCheckoutStatus } from "../lib/api";

export default function Success() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ state: "checking", message: "Confirming your payment…" });

    useEffect(() => {
        if (!sessionId) {
            setStatus({ state: "error", message: "Missing session info." });
            return;
        }
        let attempts = 0;
        const max = 10;
        const interval = 2000;
        let timer;
        const poll = async () => {
            attempts++;
            try {
                const r = await getCheckoutStatus(sessionId);
                if (r.payment_status === "paid") { setStatus({ state: "paid", message: "Payment confirmed!" }); return; }
                if (r.status === "expired") { setStatus({ state: "error", message: "Session expired. Please try again." }); return; }
                if (attempts >= max) { setStatus({ state: "pending", message: "Still processing — check your email shortly." }); return; }
                timer = setTimeout(poll, interval);
            } catch {
                if (attempts >= max) { setStatus({ state: "pending", message: "Still processing — check your email shortly." }); return; }
                timer = setTimeout(poll, interval);
            }
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-bone flex flex-col" data-testid="success-page">
            <div className="bg-ink text-bone">
                <div className="max-w-3xl mx-auto px-5 sm:px-8 py-3.5 flex items-center gap-2">
                    <span className="text-saffron text-lg leading-none">✺</span>
                    <span className="font-heading text-lg font-semibold">My Cooking <em className="text-saffron not-italic">Assistant</em></span>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center px-5 py-16">
                <div className="max-w-lg w-full text-center animate-fade-up">
                    {status.state === "checking" && (
                        <>
                            <Loader2 className="w-10 h-10 text-saffron-dark animate-spin mx-auto mb-5" />
                            <h1 className="font-heading text-3xl sm:text-4xl">Confirming your payment…</h1>
                        </>
                    )}
                    {status.state === "paid" && (
                        <>
                            <div className="w-16 h-16 rounded-full bg-saffron border-2 border-ink flex items-center justify-center mx-auto mb-7" style={{ boxShadow: '4px 4px 0 #1A1A1A' }}>
                                <CheckCircle2 className="w-9 h-9 text-ink" />
                            </div>
                            <h1 className="font-heading font-light text-4xl sm:text-5xl leading-tight">Welcome to your kitchen, <em className="text-saffron-dark font-medium">made easier.</em></h1>
                            <p className="mt-5 text-lg text-muted2">Your personalized cooking assistant is being built right now.</p>
                            <div className="mt-9 grid sm:grid-cols-2 gap-3 text-left">
                                <div className="bg-white border-2 border-ink rounded-2xl p-5">
                                    <Mail className="w-5 h-5 text-saffron-dark mb-2" />
                                    <p className="font-medium">Check your email</p>
                                    <p className="text-sm text-muted2">A welcome note is on its way.</p>
                                </div>
                                <div className="bg-white border-2 border-ink rounded-2xl p-5">
                                    <Clock className="w-5 h-5 text-saffron-dark mb-2" />
                                    <p className="font-medium">Within 24 hours</p>
                                    <p className="text-sm text-muted2">Your assistant link will arrive.</p>
                                </div>
                            </div>
                            <Link to="/" className="btn-secondary text-sm mt-10 inline-flex" data-testid="success-home">Back home</Link>
                        </>
                    )}
                    {status.state === "pending" && (
                        <>
                            <Clock className="w-10 h-10 text-saffron-dark mx-auto mb-5" />
                            <h1 className="font-heading text-3xl sm:text-4xl">Still processing…</h1>
                            <p className="mt-3 text-muted2">{status.message}</p>
                        </>
                    )}
                    {status.state === "error" && (
                        <>
                            <h1 className="font-heading text-3xl sm:text-4xl">Hmm, something's off.</h1>
                            <p className="mt-3 text-muted2">{status.message}</p>
                            <Link to="/" className="btn-saffron text-sm mt-6 inline-flex">Back home</Link>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
